import React, { useCallback, useEffect, useMemo, useState } from "react";
import Stat from "../../components/Stat";
import Loader from "../../components/Loader";
import SearchSelect from "../../components/SearchSelect";
import { errorMessage } from "../../services";
import { getAdminTracker, getSupportTracker, getSupportTrackerPeople, getTrackerStores } from "../../services/analytics";
import "./AdminTracker.css";

// Analytics -> Admin Tracker (Super Admin): everything one store's owner and staff did in the admin panel. Pick a
// store; see whether they are online, when they signed in, how long they stayed, which tabs they used, what they
// changed, and Ownezzy's suggestions on how to help them. Data: backend services/adminTracker.js.
// support: the same for one Support Admin (Super Admin only): the websites and customers they changed.
const DAYS = [{ value: 1, label: "Today" }, { value: 7, label: "7 days" }, { value: 30, label: "30 days" }, { value: 90, label: "90 days" }, { value: 180, label: "180 days" }];
const KINDS = [
  { value: "all", label: "All" },
  { value: "auth", label: "Sign-ins" },
  { value: "session", label: "Panel open / close" },
  { value: "navigation", label: "Tabs & clicks" },
  { value: "change", label: "Changes" },
  { value: "security", label: "Security" },
  { value: "account", label: "Account" },
];
const STORE_KEY = "oz_tracker_store";
const SUPPORT_KEY = "oz_tracker_support";
const LIVE_MS = 30 * 1000;

const dateTime = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const timeOnly = (d) => new Date(d).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", second: "2-digit" });
const dayLabel = (d) => new Date(d).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
const ago = (d) => {
  if (!d) return "never";
  const s = Math.round((Date.now() - new Date(d)) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} days ago`;
};
export const duration = (ms) => {
  const s = Math.round((ms || 0) / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

// The timeline as a CSV file, for sharing or keeping
function downloadCsv(report, rows) {
  const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [["Date & time", "Type", "Activity", "Details", "By"].map(cell).join(",")]
    .concat(rows.map((r) => [dateTime(r.at), r.kind, r.title, r.detail, r.who].map(cell).join(",")));
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `admin-activity-${report.store ? report.store.slug : `support-${report.person.name.toLowerCase().replace(/\W+/g, "-")}`}-${report.days}d.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function AdminTracker({ support = false }) {
  const pickKey = support ? SUPPORT_KEY : STORE_KEY;
  const [stores, setStores] = useState([]); // stores, or Support Admins in support mode
  const [slug, setSlug] = useState(() => localStorage.getItem(pickKey) || "");
  const [days, setDays] = useState(7);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [kind, setKind] = useState("all");
  const [search, setSearch] = useState("");
  const [live, setLive] = useState(true);

  useEffect(() => {
    // The first store (online or most recently signed in) opens when none was picked before
    const list = support
      ? getSupportTrackerPeople().then(({ data }) => (data.supportAdmins || []).map((p) => ({ slug: p.id, name: p.name, owner: p.email, lastLoginAt: p.lastLoginAt, online: p.online })))
      : getTrackerStores().then(({ data }) => data.stores || []);
    list.then((items) => {
      setStores(items);
      if (items[0]) setSlug((current) => (current && items.some((i) => i.slug === current) ? current : items[0].slug));
    }).catch((err) => setError(errorMessage(err)));
  }, [support]);

  const load = useCallback((quiet = false) => {
    if (!slug) return;
    if (!quiet) setLoading(true);
    (support ? getSupportTracker(slug, days) : getAdminTracker(slug, days))
      .then(({ data }) => { setReport(data); setError(""); })
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setLoading(false));
  }, [slug, days, support]);

  useEffect(() => {
    if (slug) localStorage.setItem(pickKey, slug);
    load();
  }, [load, slug, pickKey]);
  // Live: refreshed every 30 seconds while the page is on screen
  useEffect(() => {
    if (!live || !slug) return undefined;
    const timer = setInterval(() => document.visibilityState === "visible" && load(true), LIVE_MS);
    return () => clearInterval(timer);
  }, [live, slug, load]);

  const options = useMemo(() => stores.map((s) => ({ value: s.slug, label: `${s.online ? "🟢 " : ""}${s.name}`, hint: support ? `${s.owner} · last sign-in ${ago(s.lastLoginAt)}` : `${s.slug} · ${s.owner || "no owner"} · last sign-in ${ago(s.lastLoginAt)}` })), [stores, support]);
  const loadOptions = useCallback(async (q) => {
    const query = (q || "").toLowerCase();
    return options.filter((o) => !query || `${o.label} ${o.hint}`.toLowerCase().includes(query)).slice(0, 50);
  }, [options]);
  const selected = options.find((o) => o.value === slug) || null;

  const rows = useMemo(() => {
    if (!report) return [];
    const q = search.trim().toLowerCase();
    return report.timeline.filter((r) => (kind === "all" || r.kind === kind) && (!q || `${r.title} ${r.detail} ${r.who}`.toLowerCase().includes(q)));
  }, [report, kind, search]);
  // The timeline in days, newest first
  const byDay = useMemo(() => {
    const groups = [];
    rows.slice(0, 600).forEach((r) => {
      const day = dayLabel(r.at);
      if (!groups.length || groups[groups.length - 1].day !== day) groups.push({ day, rows: [] });
      groups[groups.length - 1].rows.push(r);
    });
    return groups;
  }, [rows]);

  const sum = report && report.summary;
  const maxTab = sum && sum.topTabs.length ? sum.topTabs[0].count : 1;

  return (
    <div className="admin-tracker">
      <div className="tracker-controls">
        <div className="tracker-store-pick">
          <SearchSelect label={support ? "Support Admin" : "Website"} selected={selected} onChange={(o) => o && setSlug(o.value)} loadOptions={loadOptions}
            placeholder={support ? "Search name or email…" : "Search store, slug or owner…"} emptyText={support ? "No Support Admin yet: add one in Support admins" : "No store found"} />
        </div>
        <div className="mode-switch" role="group" aria-label="Period">
          {DAYS.map((d) => <button key={d.value} className={days === d.value ? "active" : ""} onClick={() => setDays(d.value)}>{d.label}</button>)}
        </div>
        <label className="tracker-live"><input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} /> Live</label>
        <button type="button" className="outline-btn" onClick={() => load()}>↻ Refresh</button>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {!report && !error && (slug ? <Loader label="Loading admin activity" /> : <p className="field-help">{support ? "Pick a Support Admin to see what they did." : "Pick a website to see its admin activity."}</p>)}

      {report && (
        <div className={loading ? "tracker-body is-loading" : "tracker-body"}>
          {report.person ? (
          <section className="panel tracker-head">
            <div className="tracker-head-main">
              <span className={`tracker-presence ${sum.onlineNow ? "is-online" : ""}`}>{sum.onlineNow ? "Online now" : `Last seen ${ago(sum.lastSeenAt || sum.lastLoginAt)}`}</span>
              <h2>{report.person.name} <small>Support Admin</small></h2>
              <p>{[report.person.email, report.person.phone].filter(Boolean).join(" · ")}{report.person.status !== "active" ? " · paused" : ""}</p>
            </div>
            <dl className="tracker-facts">
              <div><dt>Account created</dt><dd>{dateTime(report.person.createdAt)}</dd></div>
              <div><dt>Last sign-in</dt><dd>{dateTime(sum.lastLoginAt)}</dd></div>
              <div><dt>Websites edited</dt><dd>{sum.websitesEdited}</dd></div>
              <div><dt>Customers helped</dt><dd>{sum.customersHelped}</dd></div>
            </dl>
          </section>
          ) : (
          <section className="panel tracker-head">
            <div className="tracker-head-main">
              <span className={`tracker-presence ${sum.onlineNow ? "is-online" : ""}`}>{sum.onlineNow ? "Online now" : `Last seen ${ago(sum.lastSeenAt || sum.lastLoginAt)}`}</span>
              <h2>{report.store.name} <small>{report.store.slug}</small></h2>
              <p>
                Store admin: <b>{report.owner ? report.owner.name : "—"}</b>
                {report.owner && report.owner.email ? ` · ${report.owner.email}` : ""}
                {report.owner && report.owner.phone ? ` · ${report.owner.phone}` : ""}
                {report.staff.length ? ` · ${report.staff.length} staff` : ""}
              </p>
            </div>
            <dl className="tracker-facts">
              <div><dt>Store admin created</dt><dd>{dateTime(report.owner && report.owner.createdAt)}</dd></div>
              <div><dt>Store created</dt><dd>{dateTime(report.store.createdAt)}</dd></div>
              <div><dt>Last sign-in</dt><dd>{dateTime(sum.lastLoginAt)}</dd></div>
              <div><dt>Password</dt><dd>{report.owner && report.owner.mustChangePassword ? <span className="tracker-tag warn">Temporary (never changed)</span> : <span className="tracker-tag good">Own password</span>}</dd></div>
              <div><dt>Plan</dt><dd>{report.store.plan || "—"}{report.store.expiresAt ? ` · till ${new Date(report.store.expiresAt).toLocaleDateString("en-IN")}` : ""}</dd></div>
            </dl>
          </section>
          )}

          <div className="stats-grid tracker-stats">
            <Stat label="Signed in today" value={sum.loginsToday.length} meta={sum.loginsToday.length ? sum.loginsToday.slice(0, 4).map(timeOnly).join(", ") : "Not today"} icon="🔐" />
            <Stat label="Sessions" value={sum.sessions} meta={`${sum.logins} sign-ins in this period`} icon="🟢" />
            <Stat label="Time on screen" value={duration(sum.totalActiveMs)} meta={`Avg ${duration(sum.avgActiveMs)} per session`} icon="⏱️" />
            <Stat label="Avg session length" value={duration(sum.avgSessionMs)} meta={`Longest ${duration(sum.longestSessionMs)}`} icon="⌛" />
            <Stat label="Changes made" value={sum.changes} meta={report.person ? "Websites, customers, suggestions…" : "Products, categories, website, orders…"} icon="✏️" />
            {report.person ? (
              <>
                <Stat label="Websites edited" value={sum.websitesEdited} meta="Different websites changed" icon="🌐" />
                <Stat label="Failed sign-ins" value={sum.failedLogins} meta="Wrong password or code" icon="⚠️" warn={sum.failedLogins >= 3} />
                <Stat label="Suggestion replies" value={sum.suggestionReplies} meta={`${sum.customersHelped} customers edited or reset`} icon="💬" />
              </>
            ) : (
              <>
                <Stat label="Own products" value={sum.ownProducts} meta={`${sum.products} products in the store`} icon="📦" warn={sum.ownProducts <= 0} />
                <Stat label="Failed sign-ins" value={sum.failedLogins} meta="Wrong password or code" icon="⚠️" warn={sum.failedLogins >= 3} />
                <Stat label="Password changes" value={sum.passwordChanges} meta="Changed or reset" icon="🔑" />
              </>
            )}
          </div>

          {report.recommendations.length > 0 && (
            <section className="panel tracker-recos">
              <h2>💡 Recommendations</h2>
              <ul>{report.recommendations.map((r, i) => <li key={i} className={`reco-${r.level}`}>{r.text}</li>)}</ul>
            </section>
          )}

          <div className="tracker-grid">
            <section className="panel tracker-card">
              <h2>Most used tabs</h2>
              {sum.topTabs.length ? sum.topTabs.map((t) => (
                <div className="tracker-bar" key={t.tab}>
                  <span>{t.tab}</span>
                  <i style={{ width: `${Math.max(6, (t.count / maxTab) * 100)}%` }} />
                  <b>{t.count}</b>
                </div>
              )) : <p className="field-help">No tabs opened in this period.</p>}
            </section>
            <section className="panel tracker-card">
              <h2>What they changed</h2>
              {Object.keys(sum.changesByType).length ? Object.entries(sum.changesByType).sort((a, b) => b[1] - a[1]).map(([label, count]) => (
                <div className="tracker-bar" key={label}><span>{label}</span><i style={{ width: `${Math.max(6, (count / sum.changes) * 100)}%` }} /><b>{count}</b></div>
              )) : <p className="field-help">No changes in this period.</p>}
            </section>
          </div>

          <section className="panel tracker-card">
            <h2>Sessions <small>({report.sessions.length})</small></h2>
            <div className="site-table-wrap">
              <table className="site-table">
                <thead><tr><th>OPENED</th><th>CLOSED</th><th>LENGTH</th><th>ON SCREEN</th><th>TABS</th><th>DEVICE</th><th>BY</th></tr></thead>
                <tbody>
                  {report.sessions.length ? report.sessions.map((s) => (
                    <tr key={s.sessionId}>
                      <td>{dateTime(s.startedAt)}</td>
                      <td>{s.open ? <span className="tracker-tag good">Open now</span> : s.endedAt ? dateTime(s.endedAt) : <span title="The tab was closed without saying goodbye (e.g. phone locked)">~ {dateTime(s.lastSeenAt)}</span>}</td>
                      <td>{duration(s.durationMs)}</td>
                      <td>{duration(s.activeMs)}</td>
                      <td>{s.tabViews}</td>
                      <td>{s.device}</td>
                      <td>{s.who}</td>
                    </tr>
                  )) : <tr><td colSpan="7" className="empty-row">No sessions in this period.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>

          <section className="panel tracker-card">
            <div className="tracker-timeline-head">
              <h2>Activity timeline <small>({rows.length})</small></h2>
              <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search activity…" aria-label="Search activity" />
              <button type="button" className="outline-btn" disabled={!rows.length} onClick={() => downloadCsv(report, rows)}>⬇ CSV</button>
            </div>
            <div className="tracker-chips" role="group" aria-label="Show">
              {KINDS.map((k) => <button key={k.value} className={kind === k.value ? "active" : ""} onClick={() => setKind(k.value)}>{k.label}</button>)}
            </div>
            {byDay.length ? byDay.map((g) => (
              <div className="tracker-day" key={g.day}>
                <h3>{g.day}</h3>
                <ol>
                  {g.rows.map((r, i) => (
                    <li key={`${r.at}-${i}`} className={`kind-${r.kind}`}>
                      <time>{timeOnly(r.at)}</time>
                      <span className="tracker-icon" aria-hidden="true">{r.icon}</span>
                      <div>
                        <b>{r.title}</b>{r.detail ? <span> · {r.detail}</span> : null}
                        <small>{r.who}{r.byOwnezzy ? " · by Ownezzy" : ""}</small>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )) : <p className="field-help">Nothing to show.</p>}
            {rows.length > 600 && <p className="field-help">Showing the latest 600. Download the CSV for everything.</p>}
          </section>
        </div>
      )}
    </div>
  );
}
